import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getAdminAccessToken, setAdminAccessToken, refreshAdminToken } from '../services/api';
import { AdminAuthContext, type AdminUser } from './adminAuthContext';

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      const stored = localStorage.getItem('lighthouse-admin');
      let restoredUser: AdminUser | null = null;

      if (stored) {
        try {
          const parsed = JSON.parse(stored) as AdminUser;
          if (parsed.role === 'admin') {
            restoredUser = parsed;
            if (!cancelled) setUser(restoredUser);
          } else {
            localStorage.removeItem('lighthouse-admin');
          }
        } catch {
          localStorage.removeItem('lighthouse-admin');
        }
      }

      if (restoredUser && !getAdminAccessToken()) {
        const token = await refreshAdminToken();
        if (!token && !cancelled) {
          setUser(null);
          localStorage.removeItem('lighthouse-admin');
        }
      }

      if (!cancelled) setLoading(false);
    }

    void initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback((userData: AdminUser, token: string) => {
    if (userData.role !== 'admin') return;
    setUser(userData);
    setAdminAccessToken(token);
    localStorage.setItem('lighthouse-admin', JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAdminAccessToken(null);
    localStorage.removeItem('lighthouse-admin');
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}
